import { useParams, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import type { PublicPetProfile } from '../types/Pet'
import FoundPetReport from '../components/FoundPetReport'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'

export default function TagScan() {
  const { code } = useParams<{ code: string }>()
  const [pet, setPet] = useState<PublicPetProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!code) return

    fetch(`${API_URL}/api/tags/${code}/scan`)
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(res.status === 404 ? 'not_found' : 'error')
        }
        return res.json()
      })
      .then((data) => setPet(data))
      .catch((err) => setError(err.message === 'not_found' ? 'not_found' : 'error'))
      .finally(() => setLoading(false))
  }, [code])

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pet info...</p>
        </div>
      </div>
    )
  }

  if (error || !pet) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">🏷️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {error === 'not_found' ? 'Tag Not Found' : 'Something Went Wrong'}
          </h1>
          <p className="text-gray-600 mb-8">
            {error === 'not_found'
              ? 'This tag is not registered to a pet yet.'
              : 'We couldn\'t load the pet information. Please try again.'}
          </p>
          <Link to="/" className="btn-primary">
            Go to Pet Halo
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[80vh] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {pet.isLost && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
            <div className="flex items-center">
              <span className="text-2xl mr-3">🚨</span>
              <div>
                <h2 className="text-red-800 font-bold text-lg">This Pet Is Lost</h2>
                {pet.lostSince && (
                  <p className="text-red-700 text-sm">
                    Missing since {new Date(pet.lostSince).toLocaleDateString()}
                  </p>
                )}
                {pet.lastKnownLocation && (
                  <p className="text-red-700 text-sm">
                    Last seen near: {pet.lastKnownLocation}
                  </p>
                )}
                {pet.emergencyContact && (
                  <p className="text-red-700 text-sm font-semibold mt-1">
                    Please contact the owner if you find this pet!
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="card">
          {pet.photoUrl && (
            <div className="flex justify-center mb-6">
              <img
                src={pet.photoUrl}
                alt={pet.name}
                className="w-48 h-48 rounded-full object-cover border-4 border-primary-100"
              />
            </div>
          )}

          <h1 className="text-3xl font-bold text-center text-gray-900 mb-1">
            {pet.name}
          </h1>
          <p className="text-center text-gray-500 mb-6">{pet.species}</p>

          <div className="grid grid-cols-2 gap-4 mb-6">
            {pet.breed && (
              <InfoItem label="Breed" value={pet.breed} />
            )}
            {pet.gender && (
              <InfoItem label="Gender" value={pet.gender} />
            )}
            {pet.ageInYears != null && (
              <InfoItem
                label="Age"
                value={pet.ageInYears === 0 ? 'Less than 1 year' : `${pet.ageInYears} year${pet.ageInYears !== 1 ? 's' : ''}`}
              />
            )}
          </div>

          {(pet.emergencyContact || pet.emergencyPhone) && (
            <Section title="Emergency Contact">
              {pet.emergencyContact && <p className="text-gray-700">{pet.emergencyContact}</p>}
              {pet.emergencyPhone && (
                <div className="flex gap-3 mt-2">
                  <a
                    href={`tel:${pet.emergencyPhone}`}
                    className="flex-1 flex items-center justify-center gap-2 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                  >
                    &#128222; Call
                  </a>
                  <a
                    href={`sms:${pet.emergencyPhone}`}
                    className="flex-1 flex items-center justify-center gap-2 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
                  >
                    &#128172; Text
                  </a>
                </div>
              )}
            </Section>
          )}

          {(pet.medicalNotes || pet.allergies || pet.medications) && (
            <Section title="Medical Information">
              {pet.allergies && (
                <div className="mb-2">
                  <span className="font-medium text-gray-700">Allergies: </span>
                  <span className="text-red-600">{pet.allergies}</span>
                </div>
              )}
              {pet.medications && (
                <div className="mb-2">
                  <span className="font-medium text-gray-700">Medications: </span>
                  <span className="text-gray-600">{pet.medications}</span>
                </div>
              )}
              {pet.medicalNotes && (
                <div>
                  <span className="font-medium text-gray-700">Notes: </span>
                  <span className="text-gray-600">{pet.medicalNotes}</span>
                </div>
              )}
            </Section>
          )}
        </div>

        {pet.isLost && code && (
          <FoundPetReport code={code} petName={pet.name} />
        )}

        <p className="text-center text-gray-400 text-sm mt-6">
          Powered by <Link to="/" className="text-primary-600 hover:underline">Pet Halo</Link>
        </p>
      </div>
    </div>
  )
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="text-gray-900 font-medium">{value}</dd>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-gray-100 pt-4 mt-4">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</h3>
      {children}
    </div>
  )
}
